import { Modal, Form, Button } from "react-bootstrap";
import { useCallback, useEffect, useState } from "react";
import DeleteModal from "../../pages/delete-modal";
import { CustomModal } from "../custom-modal";
import axios from "axios";

export const FilterModal = ({
  show,
  categoryId,
  categoryName,
  filters = [],
  onClose,
  onSave,
  onSuccess,
  handleDeleteSubscription
}) => {
  const [entries, setEntries] = useState([]);
  const [allMakes, setAllMakes] = useState([]);
  const [makeSuggestions, setMakeSuggestions] = useState({});
  const [modelSuggestions, setModelSuggestions] = useState({});
  const [modelOptions, setModelOptions] = useState({});
  const [modelFocusIndex, setModelFocusIndex] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [originalRow, setOriginalRow] = useState(null);
  const [deleteInfo, setDeleteInfo] = useState({ show: false, type: "", index: null });
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");

  const isAuto = categoryId === 1;

  useEffect(() => {
    if (!show) {
      setEditingIndex(null);
      setOriginalRow(null);
    }
  }, [show]);

  const getEmptyEntry = useCallback(
    () =>
      isAuto
        ? { make: "", model: "", vin: "", customMake: "", customModel: "" }
        : { brand: "", product_name: "", upc_code: "" },
    [isAuto]
  );

  useEffect(() => {
    setEntries(filters.length > 0 ? filters : [getEmptyEntry()]);
    if (isAuto) fetchAllMakes();
  }, [filters, categoryId, getEmptyEntry, isAuto]);

  const fetchAllMakes = async () => {
    try {
      const res = await axios.get(
        `https://vpic.nhtsa.dot.gov/api/vehicles/getallmakes?format=json`
      );
      const makes = res.data.Results.map((item) => item.Make_Name);

      setAllMakes([...makes, "Other"]);
    } catch (err) {
      console.error("Error fetching makes", err);
    }
  };

  const fetchModelsForMake = async (index, make) => {
    try {
      const res = await axios.get(
        `https://vpic.nhtsa.dot.gov/api/vehicles/getmodelsformake/${make}?format=json`
      );
      const models = res.data.Results.map((item) => item.Model_Name);
      setModelOptions((prev) => ({
        ...prev,
        [index]: models
      }));
      setModelSuggestions((prev) => ({
        ...prev,
        [index]: [...models, "Other"]
      }));
    } catch (err) {
      console.error("Error fetching models", err);
      setModelOptions((prev) => ({ ...prev, [index]: [] }));
      setModelSuggestions((prev) => ({ ...prev, [index]: ["Other"] }));
    }
  };

  const handleChange = (index, field, value) => {
    const updated = [...entries];

    updated[index] = {
      ...updated[index],
      [field]: value
    };

    setEntries(updated);

    if (isAuto && field === "make") {
      if (value.length > 0) {
        let filtered = allMakes.filter((make) =>
          make.toLowerCase().startsWith(value.toLowerCase())
        );
        if (!filtered.includes("Other")) {
          filtered = [...filtered, "Other"];
        }
        setMakeSuggestions((prev) => ({
          ...prev,
          [index]: filtered
        }));
      } else {
        setMakeSuggestions((prev) => ({
          ...prev,
          [index]: ["Other"]
        }));
      }
      if (value.toLowerCase() !== "other") {
        fetchModelsForMake(index, value);
      } else {
        // Clear any previously fetched models
        setModelOptions((prev) => ({
          ...prev,
          [index]: []
        }));
      }
    }

    // if (isAuto && field === "make" && value.toLowerCase() !== "other") {
    //   fetchModelsForMake(index, value);
    // }

    if (isAuto && field === "model") {
      const makeEntry = entries[index];
      if (makeEntry.make && makeEntry.make !== "Other") {
        const allModels = modelOptions[index] || [];

        if (value.length > 0) {
          const filtered = allModels.filter((model) =>
            model.toLowerCase().includes(value.toLowerCase())
          );

          // Always include "Other" in suggestions
          let suggestions = [...filtered];
          if (!suggestions.includes("Other")) {
            suggestions = [...suggestions, "Other"];
          }

          setModelSuggestions((prev) => ({
            ...prev,
            [index]: suggestions
          }));
        } else {
          // If empty, show all models + Other
          setModelSuggestions((prev) => ({
            ...prev,
            [index]: [...allModels, "Other"]
          }));
        }
      }
    }
  };

  const handleSelectMake = (index, selectedMake) => {
    const updated = [...entries];
    updated[index].make = selectedMake;
    updated[index].model = "";
    updated[index].customMake = "";
    updated[index].customModel = "";
    setEntries(updated);

    if (selectedMake.toLowerCase() !== "other") {
      fetchModelsForMake(index, selectedMake);
    }

    setMakeSuggestions((prev) => ({
      ...prev,
      [index]: []
    }));
  };

  const isDuplicate = (entry, entries) => {
    return entries.some((e) => {
      if (entry === e) return false;

      if (isAuto) {
        const make1 = entry.make === "Other" ? entry.customMake : entry.make;
        const model1 = entry.model === "Other" ? entry.customModel : entry.model;
        const vin1 = entry.vin?.trim();

        const make2 = e.make === "Other" ? e.customMake : e.make;
        const model2 = e.model === "Other" ? e.customModel : e.model;
        const vin2 = e.vin?.trim();

        // Duplicate only if Make+Model matches AND VINs are the same or both empty
        if (make1 && model1 && make1 === make2 && model1 === model2) {
          if (!vin1 && !vin2) return true; // both VIN empty -> duplicate
          if (vin1 && vin2 && vin1 === vin2) return true; // same VIN -> duplicate
        }
        return false;
      } else {
        const brand1 = entry.brand;
        const product1 = entry.product_name;
        const upc1 = entry.upc_code?.trim();

        const brand2 = e.brand;
        const product2 = e.product_name;
        const upc2 = e.upc_code?.trim();

        if (brand1 && product1 && brand1 === brand2 && product1 === product2) {
          if (!upc1 && !upc2) return true; // both empty UPC -> duplicate
          if (upc1 && upc2 && upc1 === upc2) return true; // same UPC -> duplicate
        }
        return false;
      }
    });
  };

  const handleCustomModalClose = () => {
    setShowCustomModal(false);

    // Reset the last entry to empty after modal closes
    const updated = [...entries];
    updated[updated.length - 1] = getEmptyEntry();
    setEntries(updated);
  };

  const handleAdd = () => {
    // Add a new empty entry, if the last entry is not empty
    if (entries.length > 0 && Object.values(entries[entries.length - 1]).every((val) => val === "")) {
      return;
    }

    const lastIndex = entries.length - 1;
    const lastEntry = entries[lastIndex];

    if (isDuplicate(lastEntry, entries, lastIndex)) {
      setModalMessage("The Vehicle/Product is already added.");
      setShowCustomModal(true);
      return;
    }
    setEntries([...entries, getEmptyEntry()]);
  };

  const handleEditClick = (index) => {
    setEditingIndex(index);
    setOriginalRow({ ...entries[index] });
  };

  const handleSaveRowEdit = (index) => {
    const entry = entries[index];

    // Check for duplicates (excluding the current row being edited)
    const otherEntries = entries.filter((_, i) => i !== index);
    if (isDuplicate(entry, otherEntries)) {
      setModalMessage("The Vehicle/Product is already added.");
      setShowCustomModal(true);
      return;
    }

    setEditingIndex(null);
    setOriginalRow(null);

    // Save the changes immediately
    const cleaned = entries
      .map((entry) => {
        if (isAuto) {
          return {
            make: entry.make === "Other" ? entry.customMake : entry.make,
            model: entry.model === "Other" ? entry.customModel : entry.model,
            vin: entry.vin
          };
        }
        return entry;
      })
      .filter((entry) =>
        Object.values(entry).some((val) => val && val.trim() !== "")
      );

    onSave(cleaned);
  };

  const handleCancelRowEdit = (index) => {
    const updated = [...entries];
    updated[index] = { ...originalRow };
    setEntries(updated);
    setEditingIndex(null);
    setOriginalRow(null);
  };

  // Helper function to check if row is in edit mode
  const isRowEditing = (index) => {
    return editingIndex === index;
  };

  const handleRemoveClick = (index) => {
    setDeleteInfo({ show: true, type: "single", index });
  };

  const handleDeleteAllClick = () => {
    setDeleteInfo({ show: true, type: "all", index: null });
  };

  // Confirm deletion
  const confirmDelete = () => {
    if (deleteInfo.type === "single") {
      const updatedEntries = entries.filter((_, i) => i !== deleteInfo.index);
      setEntries(updatedEntries);
      onSave(updatedEntries);
      if (onSuccess) onSuccess();
      onClose();
    } else if (deleteInfo.type === "all") {
      handleDeleteSubscription(categoryId);
      onClose();
    }
    setDeleteInfo({ show: false, type: "", index: null });
  };

  const handleSave = () => {
    // Check for any duplicate entries before saving
    const hasDuplicates = entries.some((entry, index) => isDuplicate(entry, entries, index));

    if (hasDuplicates) {
      setModalMessage("The Vehicle/Product is already added.");
      setShowCustomModal(true);
      return;
    }
    const cleaned = entries
      .map((entry) => {
        if (isAuto) {
          return {
            make: entry.make === "Other" ? entry.customMake : entry.make,
            model: entry.model === "Other" ? entry.customModel : entry.model,
            vin: entry.vin
          };
        }
        return entry;
      })
      .filter((entry) =>
        Object.values(entry).some((val) => val && val.trim() !== "")
      );

    onSave(cleaned);
    onClose();
  };

  // Disbale button entries with make or brand empty
  const btnDisabled = entries.some((entry) => {
    if (isAuto) {
      return !entry.make;
    }
    return !entry.brand;
  });

  // Helper: check if entry is already saved in filters
  const isRowSaved = (index) => {
    // If index is beyond the saved filters, it's a new entry
    if (index >= filters.length) return false;

    // If this row is currently being edited, still consider it as saved
    if (editingIndex === index) return true;

    // For indexes within the saved filters range, check if it matches
    const entry = entries[index];
    const savedEntry = filters[index];

    if (!savedEntry) return false;

    if (isAuto) {
      return entry.make === savedEntry.make &&
        entry.model === savedEntry.model &&
        entry.vin === savedEntry.vin;
    } else {
      return entry.brand === savedEntry.brand &&
        entry.product_name === savedEntry.product_name &&
        entry.upc_code === savedEntry.upc_code;
    }
  };

  const savedCount = entries.filter((_, index) => isRowSaved(index)).length;
  const deleteAllDisabled = savedCount === 0;

  return (
    <>
      <Modal
        show={show}
        onHide={onClose}
        centered
        size="xl"
        dialogClassName="custom-modal-xl">
        <Modal.Header closeButton>
          <Modal.Title>
            Manage {categoryName} {getCategoryName(categoryId)} (Notify for recalls of selected brands)
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {entries.map((entry, index) => {
            const isSaved = isRowSaved(index);
            const isEditing = isRowEditing(index);

            return (
              <div
                key={index}
                className="d-flex align-items-start gap-2 mb-3 flex-wrap position-relative w-100">
                {isAuto ? (
                  <>
                    {/* Make input with suggestions */}
                    <div style={{ position: "relative", width: "30%" }}>
                      <Form.Control
                        placeholder="Make"
                        value={entry.make || ""}
                        onChange={(e) =>
                          handleChange(index, "make", e.target.value)
                        }
                        className={isEditing ? "editing-input" : ""}
                        autoComplete="off"
                        readOnly={isSaved && !isEditing}
                        style={(isSaved && !isEditing) ? { cursor: 'default' } : {}}
                        onFocus={(e) => {
                          if (isSaved && !isEditing) e.target.blur();
                        }}
                      />
                      {(!isSaved || isEditing) && makeSuggestions[index]?.length > 0 && (
                        <div
                          className="position-absolute bg-white border shadow-sm"
                          style={{
                            zIndex: 10,
                            maxHeight: 350,
                            overflowY: "auto",
                            width: "100%"
                          }}>
                          {[...makeSuggestions[index]].map((suggestion, idx) => (
                            <div
                              key={idx}
                              onClick={() => handleSelectMake(index, suggestion)}
                              style={{
                                padding: "5px 10px",
                                cursor: "pointer",
                                borderBottom: "1px solid #eee"
                              }}>
                              {suggestion}
                            </div>
                          ))}
                        </div>
                      )}
                      {entry.make === "Other" && (
                        <Form.Control
                          placeholder="Enter Make"
                          value={entry.customMake || ""}
                          onChange={(e) =>
                            handleChange(index, "customMake", e.target.value)
                          }
                          className={`mt-1 ${isEditing ? "editing-input" : ""}`}
                          readOnly={isSaved && !isEditing}
                          style={(isSaved && !isEditing) ? { cursor: 'default' } : {}}
                          onFocus={(e) => {
                            if (isSaved && !isEditing) e.target.blur();
                          }}
                        />
                      )}
                    </div>

                    {/* Model input */}
                    <div style={{ position: "relative", width: "30%" }}>
                      <Form.Control
                        placeholder="Model"
                        value={entry.model || ""}
                        onChange={(e) =>
                          handleChange(index, "model", e.target.value)
                        }
                        className={isEditing ? "editing-input" : ""}
                        onFocus={(e) => {
                          if (isSaved && !isEditing) {
                            e.target.blur(); // Prevent focus for saved entries
                          } else if (entry.make && entry.make !== "Other") {
                            setModelFocusIndex(index);
                          }
                        }}
                        onBlur={() => {
                          setTimeout(() => setModelFocusIndex(null), 200);
                        }}
                        autoComplete="off"
                        readOnly={isSaved && !isEditing}
                        style={(isSaved && !isEditing) ? { cursor: 'default' } : {}}
                      />
                      {(!isSaved || isEditing) && modelFocusIndex === index &&
                        modelSuggestions[index]?.length > 0 && (
                          <div
                            className="position-absolute bg-white border shadow-sm"
                            style={{
                              zIndex: 10,
                              maxHeight: 150,
                              overflowY: "auto",
                              width: "100%"
                            }}
                            onMouseDown={(e) => e.preventDefault()}
                          >
                            {modelSuggestions[index].map((model, idx) => (
                              <div
                                key={idx}
                                onMouseDown={() => {
                                  handleChange(index, "model", model);
                                  setModelFocusIndex(null);
                                }}
                                style={{
                                  padding: "5px 10px",
                                  cursor: "pointer",
                                  borderBottom: "1px solid #eee"
                                }}>
                                {model}
                              </div>
                            ))}
                          </div>
                        )}
                      {entry.model === "Other" && (
                        <Form.Control
                          placeholder="Enter Model"
                          value={entry.customModel || ""}
                          onChange={(e) =>
                            handleChange(index, "customModel", e.target.value)
                          }
                          className={`mt-1 ${isEditing ? "editing-input" : ""}`}
                          readOnly={isSaved && !isEditing}
                          style={(isSaved && !isEditing) ? { cursor: 'default' } : {}}
                          onFocus={(e) => {
                            if (isSaved && !isEditing) e.target.blur();
                          }}
                        />
                      )}
                    </div>

                    <Form.Control
                      placeholder="VIN"
                      value={entry.vin || ""}
                      onChange={(e) => handleChange(index, "vin", e.target.value)}
                      className={isEditing ? "editing-input" : ""}
                      readOnly={isSaved && !isEditing}
                      style={(isSaved && !isEditing) ? {
                        width: "30%",
                        cursor: 'default'
                      } : { width: "30%" }}
                      onFocus={(e) => {
                        if (isSaved && !isEditing) e.target.blur();
                      }}
                    />
                  </>
                ) : (
                  <>
                    <Form.Control
                      placeholder="Brand"
                      value={entry.brand || ""}
                      onChange={(e) => handleChange(index, "brand", e.target.value)}
                      className={isEditing ? "editing-input" : ""}
                      style={(isSaved && !isEditing) ? {
                        width: "30%",
                        cursor: 'default'
                      } : { width: "30%" }}
                      onFocus={(e) => {
                        if (isSaved && !isEditing) e.target.blur();
                      }}
                    />
                    <Form.Control
                      placeholder="Product Name"
                      value={entry.product_name || ""}
                      onChange={(e) =>
                        handleChange(index, "product_name", e.target.value)
                      }
                      className={isEditing ? "editing-input" : ""}
                      style={(isSaved && !isEditing) ? {
                        width: "30%",
                        cursor: 'default'
                      } : { width: "30%" }}
                      onFocus={(e) => {
                        if (isSaved && !isEditing) e.target.blur();
                      }}
                    />
                    <Form.Control
                      placeholder="UPC Code"
                      value={entry.upc_code || ""}
                      onChange={(e) =>
                        handleChange(index, "upc_code", e.target.value)
                      }
                      className={isEditing ? "editing-input" : ""}
                      style={(isSaved && !isEditing) ? {
                        width: "30%",
                        cursor: 'default'
                      } : { width: "30%" }}
                      onFocus={(e) => {
                        if (isSaved && !isEditing) e.target.blur();
                      }}
                    />
                  </>
                )}

                {isSaved && (
                  <div style={{ display: "flex", gap: 6, marginTop: "2px" }}>
                    {isEditing ? (
                      <>
                        <Button
                          variant="success"
                          className="action-btn save-btn"
                          title="Save Changes"
                          size="sm"
                          onClick={() => handleSaveRowEdit(index)}
                        >
                          <i className="fa fa-check" />
                        </Button>
                        <Button
                          variant="secondary"
                          className="action-btn cancel-btn"
                          title="Cancel Editing"
                          size="sm"
                          onClick={() => handleCancelRowEdit(index)}
                        >
                          <i className="fa fa-times" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="warning"
                          className="action-btn edit-btn"
                          title="Edit"
                          size="sm"
                          onClick={() => handleEditClick(index)}
                        >
                          <i className="fa-solid fa-pen-to-square"></i>
                        </Button>
                        <Button
                          variant="danger"
                          className="action-btn delete-btn"
                          title="Delete"
                          size="sm"
                          onClick={() => handleRemoveClick(index)}>
                          <i className="fa fa-trash" />
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )
          })}

          <Button variant="secondary" onClick={handleAdd}>
            + Add {getCategoryName(categoryId)}
          </Button>
        </Modal.Body>
        <Modal.Footer>
          <Button
            disabled={btnDisabled}
            className="recallButton"
            onClick={handleSave}>
            SAVE
          </Button>
          <Button
            className="recallButtonDelete"
            onClick={handleDeleteAllClick}
            disabled={deleteAllDisabled}>
            Delete All
          </Button>
        </Modal.Footer>
      </Modal>

      <DeleteModal
        show={deleteInfo.show}
        title="Confirm Deletion"
        message={
          deleteInfo.type === "single"
            ? categoryId === 1
              ? "Are you sure to delete the vehicle details?"
              : "Are you sure to delete the product details?"
            : categoryId === 1
              ? "Are you sure to delete all vehicle details?"
              : "Are you sure to delete all product details?"
        }
        onConfirm={confirmDelete}
        onCancel={() => setDeleteInfo({ show: false, type: "", index: null })}
      />

      {showCustomModal && (
        <CustomModal
          isOpen={showCustomModal}
          onClose={handleCustomModalClose}
          heading={<strong>Alert</strong>}
          text={modalMessage}
          btnName="Ok"
          onClick={handleCustomModalClose}
        />
      )}
    </>
  );
};

const getCategoryName = (id) => {
  switch (id) {
    case 1:
      return "Vehicles";
    case 2:
    case 3:
    case 4:
    case 6:
      return "Products";
    default:
      return "";
  }
};
